import { useTranslation } from 'react-i18next'
import SVG from 'react-inlinesvg'

import type { Product, ProductConsumerCategory } from '@/lib/api.types'

import {
  TicketsBody,
  TicketsEmptyMsg,
  TicketsHeader,
  TicketsHeaderContent,
  TicketsLogo,
  TicketsMedia,
  TicketsTitle,
  TicketsView
} from '../../Tickets.styles'
import ProductCard from '../ProductCard'
import TicketCard from '../TicketCard'

interface TicketsListViewProps {
  categoryId?: number | string | null
  categoryName: string
  coverImageUrl?: string
  products: Product[]
  accessibilityMode?: boolean
  currency?: string
  cardType?: 'ticket' | 'product'
}

const TicketsListView = ({
  categoryId,
  categoryName,
  coverImageUrl,
  products,
  accessibilityMode,
  currency,
  cardType = 'ticket'
}: TicketsListViewProps) => {
  const { t } = useTranslation()

  const ticketItems = products.flatMap(product => {
    if (!Array.isArray(product.consumerCategories) || product.consumerCategories.length === 0) {
      return [{ product, consumerCategory: null as ProductConsumerCategory | null, key: String(product.productId || product.id) }]
    }

    const categories = [...product.consumerCategories].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
    return categories.map(consumerCategory => ({
      product,
      consumerCategory,
      key: `${String(product.productId || product.id)}:${consumerCategory.id}`
    }))
  })

  return (
    <TicketsView>
      <TicketsHeader high>
        <TicketsMedia>{coverImageUrl && <img src={coverImageUrl} alt=".."></img>}</TicketsMedia>
        <TicketsHeaderContent>
          <TicketsLogo>
            <SVG src={require('@/assets/img/logo.svg').default}></SVG>
          </TicketsLogo>
          <TicketsTitle>
            <h3>Zamek Królewski na Wawelu</h3>
            <h1>{categoryName}</h1>
          </TicketsTitle>
        </TicketsHeaderContent>
      </TicketsHeader>
      <TicketsBody>
        {cardType === 'product' && (
          <>
            {products.length === 0 && <TicketsEmptyMsg>{t('noTickets')}</TicketsEmptyMsg>}
            {products.map(product => (
              <ProductCard
                key={String(product.productId || product.id)}
                vertical={accessibilityMode}
                product={product}
                currency={currency}
                categoryId={categoryId ?? null}
              />
            ))}
          </>
        )}
        {cardType !== 'product' && (
          <>
            {ticketItems.length === 0 && <TicketsEmptyMsg>{t('noTickets')}</TicketsEmptyMsg>}
            {ticketItems.map(({ product, consumerCategory, key }) => (
              <TicketCard
                key={key}
                vertical={accessibilityMode}
                product={product}
                consumerCategory={consumerCategory}
                currency={currency}
              />
            ))}
          </>
        )}
      </TicketsBody>
    </TicketsView>
  )
}

export default TicketsListView
